// Locale context. The active locale comes from the signed-in user's preference,
// falls back to what the browser asks for, and is remembered locally so the
// login screen renders in the right language before there is a session.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { localeTag, translate, type TranslationKey, type TranslationVars } from '@shared/i18n/index';
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@shared/types';

const STORAGE_KEY = 'ares-invoice.locale';

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: TranslationVars) => string;
  tag: string;
}

const I18nContext = createContext<I18nValue | undefined>(undefined);

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LOCALES.includes(stored as Locale)) return stored as Locale;
  } catch {
    // Private browsing or blocked storage: fall through to the browser's choice.
  }
  for (const candidate of navigator.languages ?? [navigator.language]) {
    const base = candidate?.split('-')[0];
    if (base === 'es' || base === 'en') return base;
  }
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* storage unavailable */ }
  }, []);

  useEffect(() => { document.documentElement.lang = locale; }, [locale]);

  const value = useMemo<I18nValue>(() => ({
    locale,
    setLocale,
    t: (key, vars) => translate(locale, key, vars),
    tag: localeTag(locale),
  }), [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Shorthand for components that only need the translate function. */
export function useT() {
  return useI18n().t;
}
